import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
// Fetching models from API endpoint instead of constants
import {
  addImageDimensions,
  formatTimestamp,
  getImageProviderDisplayName,
  hostname,
  parseError,
} from "../../utils";

export default function Images({
  currentUser,
  getToken,
  eventListener,
  companyId,
}: {
  getToken: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
}) {
  const [init, setInit] = useState(false);
  const [images, setImages] = useState<Array<any>>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogLoading, setImageDialogLoading] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageSize, setImageSize] = useState("1024x1024");
  const [imageStyle, setImageStyle] = useState("vivid");
  const [imageQuality, setImageQuality] = useState("auto");
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>({});
  const [model, setModel] = useState("gpt-image-1");
  const [imageModels, setImageModels] = useState<Array<any>>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedReferenceImages, setSelectedReferenceImages] = useState<
    Array<string>
  >([]);
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<
    Array<{ id: string; url: string }>
  >([]);
  const [showImageSelector, setShowImageSelector] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteImage = async (image: any) => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .delete(`${hostname}/images/${image.id}`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        getData();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    image: any
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedMenuItem(image);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuItem(null);
  };

  const handleDelete = () => {
    if (selectedMenuItem) {
      deleteImage(selectedMenuItem);
    }
    handleMenuClose();
  };

  const handleDownload = async () => {
    if (selectedMenuItem) {
      try {
        // Fetch the image as a blob to force download
        const response = await fetch(selectedMenuItem.url);
        if (!response.ok) {
          throw new Error("Failed to download image");
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `image-${selectedMenuItem.id}.png`;
        link.click();
        window.URL.revokeObjectURL(downloadUrl);

        eventListener({
          type: "SET_SUCCESS",
          success: "Image downloaded successfully",
        });
      } catch (error) {
        console.error("Error downloading image:", error);
        eventListener({
          type: "SET_ERROR",
          error: "Failed to download image",
        });
      }
    }
    handleMenuClose();
  };

  const handlePreview = () => {
    if (selectedMenuItem) {
      previewImage(null, selectedMenuItem);
    }
    handleMenuClose();
  };

  const previewImage = (e: any, img: any) => {
    setSelectedImage(img);
    setImagePreviewOpen(true);
  };

  const createImage = async () => {
    const currentToken = await getToken();
    setImageDialogLoading(true);
    var req: any = {
      model: model,
      prompt: imagePrompt,
      companyId: companyId,
    };

    const selectedModel = imageModels.find((x) => x.id === model);

    // Add reference images if selected and model supports it
    if (
      (selectedReferenceImages.length > 0 ||
        uploadedReferenceImages.length > 0) &&
      selectedModel?.imageRef
    ) {
      // Combine platform images (IDs) and uploaded images (base64 data)
      req.referenceImages = [
        ...selectedReferenceImages,
        ...uploadedReferenceImages.map((img) => ({ url: img.url })),
      ];
    }

    if (["gpt-image-1", "gpt-image-1.5", "gpt-image-2"].includes(model)) {
      req = {
        ...req,
        style: imageStyle,
        size: imageSize,
        quality: imageQuality,
      };
    } else if (selectedModel && selectedModel.provider === "leonardo") {
      req = {
        ...req,
        type: "leonardo",
        width: selectedModel.default_width || selectedModel.modelWidth,
        height: selectedModel.default_height || selectedModel.modelHeight,
      };
    }

    axios
      .post(`${hostname}/images`, req, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        getData();
        // Only close dialog on success - keep settings for potential reuse
        setImageDialogOpen(false);
        // Don't clear settings - user might want to generate similar images
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
        // Keep dialog open on error so user can fix and retry
      })
      .finally(() => {
        setImageDialogLoading(false);
      });
  };

  const getImageModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/api/v0/images/models`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setImageModels(response.data);
      // If no current model is selected and we have models, set the first one
      if (!model && response.data.length > 0) {
        setModel(response.data[0].id);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const getData = async () => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(`${hostname}/images`, {
        params: {
          companyId,
          page: page + 1,
          pageSize,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setImages(res.data.items);
        setTotal(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fallbackImageSrc =
    "https://i0.wp.com/theantiquewarehousehudsonny.com/wp-content/uploads/2019/12/placehold-3.jpg?resize=300%2C202&ssl=1";
  const handleError = (e: any) => {
    e.target.src = fallbackImageSrc;
  };

  const toggleImageSelection = (imageId: string) => {
    setSelectedReferenceImages((prev) => {
      if (prev.includes(imageId)) {
        return prev.filter((id) => id !== imageId);
      } else {
        // Limit to 16 images for GPT-1 as per API docs
        if (prev.length >= 16) {
          eventListener({
            type: "SET_ERROR",
            error: "Maximum 16 reference images allowed",
          });
          return prev;
        }
        return [...prev, imageId];
      }
    });
  };

  const handleModelChange = (newModel: string) => {
    setModel(newModel);
    // Only clear reference images if new model doesn't support imageRef
    const newSelectedModel = imageModels.find((x) => x.id === newModel);
    if (!newSelectedModel?.imageRef) {
      setSelectedReferenceImages([]);
      setUploadedReferenceImages([]);
    }
    // Keep all other settings (prompt, quality, size, style) for reuse
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const totalImages =
      selectedReferenceImages.length +
      uploadedReferenceImages.length +
      files.length;
    if (totalImages > 16) {
      eventListener({
        type: "SET_ERROR",
        error: "Maximum 16 reference images allowed (including uploaded files)",
      });
      return;
    }

    Array.from(files).forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setUploadedReferenceImages((prev) => [
            ...prev,
            {
              id: `upload-${Date.now()}-${Math.random()}`,
              url: result,
            },
          ]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  const removeUploadedImage = (imageId: string) => {
    setUploadedReferenceImages((prev) =>
      prev.filter((img) => img.id !== imageId)
    );
  };

  const clearAllSettings = () => {
    setImagePrompt("");
    setImageSize("1024x1024");
    setImageStyle("vivid");
    setImageQuality("auto");
    setSelectedReferenceImages([]);
    setUploadedReferenceImages([]);
  };

  const copyPromptToClipboard = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      eventListener({
        type: "SET_SUCCESS",
        success: "Prompt copied to clipboard!",
      });
    } catch (err) {
      console.error("Failed to copy prompt:", err);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to copy prompt to clipboard",
      });
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();
      getImageModels();
    }
  }, [currentUser, companyId, page]);

  // Removed the loading blocker - now showing loader under Create Image button

  return (
    <Box>
      <div style={{ width: "100%", minHeight: 40 }}>
        <Button
          data-tutorial="generate-image"
          variant="contained"
          style={{ marginTop: 10 }}
          startIcon={<ImageOutlined />}
          onClick={() => setImageDialogOpen(true)}
        >
          Create Image
        </Button>
        <IconButton
          onClick={() => getData()}
          style={{ float: "right", marginTop: 10 }}
        >
          <RefreshOutlined />
        </IconButton>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </div>
      {images.length == 0 && !loading && (
        <Box sx={{ mt: 3 }}>
          <Typography>No Images found.</Typography>
        </Box>
      )}
      <TableContainer data-tutorial="image-gallery">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Thumbnail</TableCell>
              <TableCell>Prompt</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {images.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={addImageDimensions(item.url, 112)}
                    sx={{ width: 56, height: 56, cursor: "pointer" }}
                    onClick={(e) => previewImage(e, item)}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => copyPromptToClipboard(item.prompt)}
                      sx={{ p: 0.5 }}
                      title="Copy prompt"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "300px",
                      }}
                    >
                      {item.prompt}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Chip
                      label={getImageProviderDisplayName(
                        item.provider || "openai"
                      )}
                      size="small"
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {item.model || "Unknown Model"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {formatTimestamp(item.created).fullDateTime}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, item)}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handlePreview}>
          <ImageOutlined fontSize="small" sx={{ mr: 1 }} />
          Preview
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)}>
        <DialogTitle>
          Create Image
          <IconButton
            aria-label="close"
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Describe the image you want generated in the prompt
          </DialogContentText>
          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel>Model</InputLabel>
            <Select
              label="Model"
              value={model}
              onChange={(e) => handleModelChange(e.target.value)}
            >
              {imageModels.map((imageModel, index) => (
                <MenuItem value={imageModel.id} key={index}>
                  <Chip
                    label={getImageProviderDisplayName(imageModel.provider)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  {imageModel.name}
                  {imageModel.credits && ` (${imageModel.credits} credits)`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            style={{ marginTop: 20 }}
            label="Prompt"
            value={imagePrompt}
            fullWidth
            multiline
            rows={3}
            onChange={(e: any) => setImagePrompt(e.target.value)}
          />
          {/* Show reference image selector for models that support it */}
          {imageModels.find((x) => x.id === model)?.imageRef && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowImageSelector(!showImageSelector)}
                fullWidth
              >
                {showImageSelector ? "Hide" : "Select"} Reference Images
                {selectedReferenceImages.length +
                  uploadedReferenceImages.length >
                  0 &&
                  ` (${
                    selectedReferenceImages.length +
                    uploadedReferenceImages.length
                  })`}
              </Button>
              {showImageSelector && (
                <Box sx={{ mt: 2 }}>
                  {/* Upload from computer button */}
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      component="label"
                      size="small"
                    >
                      Upload from Computer
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={handleFileUpload}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Max 16 images total
                    </Typography>
                  </Box>

                  {/* Show uploaded images */}
                  {uploadedReferenceImages.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Uploaded Images
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {uploadedReferenceImages.map((img) => (
                          <Box
                            key={img.id}
                            sx={{
                              position: "relative",
                              border: "2px solid #1976d2",
                              borderRadius: 1,
                              p: 0.5,
                            }}
                          >
                            <Avatar
                              variant="rounded"
                              src={img.url}
                              sx={{ width: 56, height: 56 }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: -8,
                                right: -8,
                                backgroundColor: "white",
                                "&:hover": { backgroundColor: "#f5f5f5" },
                                padding: "2px",
                              }}
                              onClick={() => removeUploadedImage(img.id)}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Platform images selector */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Platform Images
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflow: "auto",
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {images.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No images available. Create some images first to use as
                        references.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {images.map((img) => (
                          <Box
                            key={img.id}
                            onClick={() => toggleImageSelection(img.id)}
                            sx={{
                              position: "relative",
                              cursor: "pointer",
                              border: selectedReferenceImages.includes(img.id)
                                ? "2px solid #1976d2"
                                : "2px solid transparent",
                              borderRadius: 1,
                              p: 0.5,
                            }}
                          >
                            <Avatar
                              variant="rounded"
                              src={addImageDimensions(img.url, 112)}
                              sx={{ width: 56, height: 56 }}
                            />
                            {selectedReferenceImages.includes(img.id) && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  backgroundColor: "#1976d2",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: 20,
                                  height: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                }}
                              >
                                ✓
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {["gpt-image-1", "gpt-image-1.5", "gpt-image-2"].includes(model) && (
            <React.Fragment>
              <FormControl fullWidth style={{ marginTop: 20 }}>
                <InputLabel>Quality</InputLabel>
                <Select
                  label="Quality"
                  value={imageQuality}
                  onChange={(e) => setImageQuality(e.target.value)}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth style={{ marginTop: 20 }}>
                <InputLabel>Size</InputLabel>
                <Select
                  label="Size"
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value)}
                >
                  <MenuItem value="1024x1024">1024x1024</MenuItem>
                  <MenuItem value="1536x1024">1536x1024 (wide)</MenuItem>
                  <MenuItem value="1024x1536">1024x1536 (tall)</MenuItem>
                </Select>
              </FormControl>
            </React.Fragment>
          )}
        </DialogContent>
        <DialogActions>
          <Chip
            label={`${imageModels.find((x) => x.id === model)?.credits || 5} Credits`}
            style={{ marginRight: "auto" }}
          />
          <Button onClick={clearAllSettings} color="secondary" size="small">
            Clear All
          </Button>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={createImage}
            loading={imageDialogLoading}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        maxWidth="xl"
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
      >
        <DialogTitle>{selectedImage.prompt}</DialogTitle>
        <DialogContent>
          <img src={/^https?:\/\/[a-zA-Z0-9]/.test(selectedImage.url) ? selectedImage.url : ""} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
